import React, { useState } from 'react';

import { CheckIcon, Face, XIcon } from './Face';
import styles from './CustomerSurveyModal.module.scss';
import { useEscapeKey } from './hooks/useEscapeKey';
import { useLockScroll } from './hooks/useLockScroll';
import { CSAT_L } from './types';
import type { SurveyConfig } from './types';

export type CustomerSurveySubmitPayload = {
  recommend: number;
  satisfaction: number;
  comment: string;
};

export type CustomerSurveySubmitResult = {
  thanksTitle?: string;
  thanksBody?: string;
};

export type CustomerSurveyModalProps = {
  config: SurveyConfig;
  onDismiss: () => void;
  onSubmit: (payload: CustomerSurveySubmitPayload) => Promise<CustomerSurveySubmitResult>;
};

export default function CustomerSurveyModal({ config, onDismiss, onSubmit }: CustomerSurveyModalProps) {
  const [rec, setRec] = useState(0);
  const [sat, setSat] = useState(0);
  const [text, setText] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [thanksTitle, setThanksTitle] = useState(config.thanksTitle);
  const [thanksBody, setThanksBody] = useState(config.thanksBody);

  const answered = (rec ? 1 : 0) + (sat ? 1 : 0) + (text.trim() ? 1 : 0);
  const canSubmit = rec > 0 && sat > 0;

  useLockScroll();
  useEscapeKey(() => {
    if (done) onDismiss();
  });

  const finish = async (submitted: boolean) => {
    if (!submitted) {
      onDismiss();
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const result = await onSubmit({
        recommend: rec,
        satisfaction: sat,
        comment: text.trim(),
      });
      if (result.thanksTitle) setThanksTitle(result.thanksTitle);
      if (result.thanksBody) setThanksBody(result.thanksBody);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      role="presentation"
      onClick={() => {
        if (done) onDismiss();
      }}
    >
      <div
        className={styles.card}
        role="dialog"
        aria-label={config.title}
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div className={styles.doneWrap}>
            <button
              type="button"
              className={`${styles.close} ${styles.doneClose}`}
              onClick={onDismiss}
              aria-label="Close"
            >
              <XIcon />
            </button>
            <div className={styles.doneMark}>
              <CheckIcon className={styles.doneMarkIcon} />
            </div>
            <h4>{thanksTitle}</h4>
            <p>{thanksBody}</p>
          </div>
        ) : (
          <>
            <div className={styles.head}>
              <div className={styles.headTop}>
                <div className={styles.progress}>
                  <span className={`${styles.dot} ${rec ? styles.dotOn : ''}`} />
                  <span className={`${styles.dot} ${sat ? styles.dotOn : ''}`} />
                  <span className={`${styles.dot} ${text.trim() ? styles.dotOn : ''}`} />
                  <span className={styles.progLbl}>{answered} of 3 answered</span>
                </div>
                <button type="button" className={styles.close} onClick={onDismiss} aria-label="Close">
                  <XIcon />
                </button>
              </div>
              <h4 className={styles.title}>{config.title}</h4>
              <p className={styles.subtitle}>{config.subtitle}</p>
            </div>
            <div className={styles.body}>
              <div className={styles.question}>
                <p className={styles.questionLabel}>{config.q1}</p>
                <div className={styles.scale} role="radiogroup" aria-label={config.q1}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      data-s={n}
                      role="radio"
                      aria-checked={rec === n}
                      className={`${styles.scaleOpt} ${rec === n ? styles.scaleOptOn : ''}`}
                      onClick={() => setRec(n)}
                      disabled={submitting}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className={styles.anchors}>
                  <span>Not at all likely</span>
                  <span>Extremely likely</span>
                </div>
              </div>
              <div className={styles.question}>
                <p className={styles.questionLabel}>{config.q2}</p>
                <div className={styles.satRow} role="radiogroup" aria-label={config.q2}>
                  {CSAT_L.map((l, i) => (
                    <button
                      type="button"
                      key={l}
                      data-s={i + 1}
                      role="radio"
                      aria-checked={sat === i + 1}
                      className={`${styles.satOpt} ${sat === i + 1 ? styles.satOptOn : ''}`}
                      onClick={() => setSat(i + 1)}
                      disabled={submitting}
                    >
                      <span className={styles.face}>
                        <Face i={i} />
                      </span>
                      <span className={styles.satLbl}>
                        {l === 'Very dissatisfied' ? l.replace(' ', '\n') : l}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.question}>
                <p className={styles.questionLabel}>
                  {config.q3}
                  <span className={styles.optional}>Optional</span>
                </p>
                <div className={styles.textareaWrap}>
                  <textarea
                    className={styles.textarea}
                    maxLength={500}
                    value={text}
                    onChange={(e) => setText(e.target.value.replace(/^\s+/, ''))}
                    placeholder="Tell us what's on your mind…"
                    disabled={submitting}
                  />
                  <span className={styles.charCount}>{text.trim().length} / 500</span>
                </div>
              </div>
            </div>
            {error ? <p className={styles.errorMsg}>{error}</p> : null}
            <div className={styles.footer}>
              <button type="button" className={styles.linkBtn} onClick={() => finish(false)} disabled={submitting}>
                Maybe later
              </button>
              <button
                type="button"
                className={styles.submitBtn}
                disabled={!canSubmit || submitting}
                onClick={() => finish(true)}
              >
                {submitting ? 'Submitting…' : 'Submit feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
